/**
 * Datas sensíveis a fuso horário. "Hoje" é sempre o dia no fuso da loja
 * (padrão America/Sao_Paulo), nunca o do servidor.
 */

/** Offset (em minutos) do fuso `tz` no instante `date`. Ex.: São Paulo -> -180. */
export function tzOffsetMinutes(date: Date, tz: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = Object.fromEntries(dtf.formatToParts(date).map((p) => [p.type, p.value]));
  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return Math.round((asUTC - date.getTime()) / 60000);
}

/** "2026-09-29" (data local no fuso) */
export function localDateKey(date: Date, tz: string): string {
  const d = new Date(date.getTime() + tzOffsetMinutes(date, tz) * 60000);
  return d.toISOString().slice(0, 10);
}

/** Instante UTC do início do dia local `yyyy-mm-dd` no fuso. */
export function startOfLocalDay(dateKey: string, tz: string): Date {
  const [y, m, d] = dateKey.split('-').map(Number) as [number, number, number];
  const guess = new Date(Date.UTC(y, m - 1, d, 0, 0, 0));
  const offset = tzOffsetMinutes(guess, tz);
  return new Date(guess.getTime() - offset * 60000);
}

export function addDaysKey(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}

export type PeriodPreset = 'today' | 'yesterday' | '7d' | '30d' | 'this_month' | 'last_month' | '90d' | 'this_year' | 'custom';

export const PERIOD_LABELS: Record<PeriodPreset, string> = {
  today: 'Hoje',
  yesterday: 'Ontem',
  '7d': 'Últimos 7 dias',
  '30d': 'Últimos 30 dias',
  this_month: 'Este mês',
  last_month: 'Mês passado',
  '90d': 'Últimos 90 dias',
  this_year: 'Este ano',
  custom: 'Personalizado',
};

/** Resolve um período para [from, to) em datas locais (to exclusivo). */
export function resolvePeriod(
  preset: PeriodPreset,
  tz: string,
  now: Date = new Date(),
  custom?: { from: string; to: string },
): { from: string; to: string } {
  const today = localDateKey(now, tz);
  const [y, m] = today.split('-').map(Number) as [number, number];
  const pad = (n: number) => String(n).padStart(2, '0');
  switch (preset) {
    case 'today':
      return { from: today, to: addDaysKey(today, 1) };
    case 'yesterday':
      return { from: addDaysKey(today, -1), to: today };
    case '7d':
      return { from: addDaysKey(today, -6), to: addDaysKey(today, 1) };
    case '30d':
      return { from: addDaysKey(today, -29), to: addDaysKey(today, 1) };
    case '90d':
      return { from: addDaysKey(today, -89), to: addDaysKey(today, 1) };
    case 'this_month':
      return { from: `${y}-${pad(m)}-01`, to: addDaysKey(today, 1) };
    case 'last_month': {
      const first = `${y}-${pad(m)}-01`;
      const prevFirst = m === 1 ? `${y - 1}-12-01` : `${y}-${pad(m - 1)}-01`;
      return { from: prevFirst, to: first };
    }
    case 'this_year':
      return { from: `${y}-01-01`, to: addDaysKey(today, 1) };
    case 'custom':
      if (!custom) return { from: today, to: addDaysKey(today, 1) };
      return { from: custom.from, to: addDaysKey(custom.to, 1) };
  }
}

/** Período anterior de mesmo tamanho (para comparação "vs período anterior"). */
export function previousPeriod(range: { from: string; to: string }): { from: string; to: string } {
  const days = Math.round((Date.parse(range.to) - Date.parse(range.from)) / 86400000);
  return { from: addDaysKey(range.from, -days), to: range.from };
}

export function formatDate(value: string | Date | null | undefined, tz = 'America/Sao_Paulo'): string {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', { timeZone: tz, day: '2-digit', month: '2-digit', year: 'numeric' }).format(
    new Date(value),
  );
}

export function formatDateTime(value: string | Date | null | undefined, tz = 'America/Sao_Paulo'): string {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: tz,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export function formatTime(value: string | Date | null | undefined, tz = 'America/Sao_Paulo'): string {
  if (!value) return '';
  return new Intl.DateTimeFormat('pt-BR', { timeZone: tz, hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}

/** "há 5 min", "ontem", "12/09" */
export function formatRelative(value: string | Date | null | undefined, now: Date = new Date()): string {
  if (!value) return '';
  const d = new Date(value);
  const diff = (now.getTime() - d.getTime()) / 1000;
  if (diff < 45) return 'agora';
  if (diff < 3600) return `há ${Math.round(diff / 60)} min`;
  if (diff < 86400) return `há ${Math.round(diff / 3600)} h`;
  if (diff < 172800) return 'ontem';
  if (diff < 604800) return `há ${Math.round(diff / 86400)} dias`;
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(d);
}
